import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, SignIn, Eye, EyeSlash } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

function formatError(detail) {
  if (!detail) return "Erreur inconnue. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || String(detail);
}

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user && user.role === "admin") return <Navigate to="/admin/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || "Impossible de se connecter.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bokeh-strong px-4 py-16" data-testid="admin-login-page">
      <motion.form
        onSubmit={onSubmit}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md rounded-2xl bg-[#0A0A0A] border border-[#C9A962]/30 p-8 md:p-10 shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
      >
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-full bg-[#C9A962]/15 border border-[#C9A962]/50 mx-auto mb-4 flex items-center justify-center">
            <Lock size={24} weight="fill" className="text-[#C9A962]" />
          </div>
          <p className="text-xs tracking-[0.3em] text-[#C9A962] mb-2">ESPACE RÉSERVÉ</p>
          <h1 className="text-2xl md:text-3xl font-extrabold text-white">
            Administration <span className="gradient-text">Pixora</span>
          </h1>
        </div>

        <label className="block mb-4">
          <span className="text-[#C9A962] text-xs font-semibold mb-2 uppercase tracking-wider block">Email</span>
          <input
            type="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@pixora..."
            className="w-full bg-black border border-[#C9A962]/30 rounded-xl px-4 py-3 text-white placeholder:text-[#5A5040] focus:border-[#C9A962] focus:outline-none focus:ring-2 focus:ring-[#C9A962]/30"
            data-testid="admin-login-email"
          />
        </label>

        <label className="block mb-6">
          <span className="text-[#C9A962] text-xs font-semibold mb-2 uppercase tracking-wider block">Mot de passe</span>
          <div className="relative">
            <input
              type={showPwd ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full bg-black border border-[#C9A962]/30 rounded-xl pl-4 pr-11 py-3 text-white placeholder:text-[#5A5040] focus:border-[#C9A962] focus:outline-none focus:ring-2 focus:ring-[#C9A962]/30"
              data-testid="admin-login-password"
            />
            <button
              type="button"
              onClick={() => setShowPwd((v) => !v)}
              aria-label={showPwd ? "Masquer" : "Afficher"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A7A5A] hover:text-[#C9A962]"
            >
              {showPwd ? <EyeSlash size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </label>

        {error && (
          <div className="mb-4 rounded-lg bg-red-900/30 border border-red-700/50 px-4 py-2 text-sm text-red-300" data-testid="admin-login-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="w-full py-3.5 rounded-full font-bold text-black bg-gradient-to-r from-[#D4BA7A] to-[#C9A962] hover:shadow-[0_0_30px_rgba(201,169,98,0.6)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <span className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
              Connexion…
            </>
          ) : (
            <>
              <SignIn size={18} weight="bold" />
              Se connecter
            </>
          )}
        </button>

        <p className="text-[11px] text-center text-[#5A5040] mt-6">
          Accès réservé — 5 échecs = verrouillage 15 min.
        </p>
      </motion.form>
    </div>
  );
}
